import { Link } from "react-router-dom";
import { productPath } from "../../../lib/productUrls";
import { Plus, Check } from "lucide-react";
import { isOnSale, effectivePrice } from "../../../lib/price";
import { imgSrc } from "./completeTheLookMatch";

const fmtINR = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

/**
 * RecoCard — the single, elevated recommendation tile shared by every PDP /
 * cross-sell rail (CompleteTheLook, YouMayLove, RecentlyViewed) and the
 * add-to-bag cross-sell modal.
 *
 * Design cues are taken from the main catalog ProductCard (rounded-2xl image,
 * rosegold category overline, sale-aware price block) but COMPACT for a
 * horizontal carousel — no wishlist heart, no size row, no try-on pill.
 *
 * Elevation is a warm wine-tinted shadow (never a heavy/gray border), a soft
 * hover zoom on the image, and a subtle lift. This replaces the four
 * near-duplicate basic cards that had drifted apart.
 *
 * Price logic mirrors ProductCard exactly via the shared lib/price helpers:
 *   effective (sale) price · struck MRP · "Save ₹X" chip — but only when the
 *   piece is actually on sale.
 *
 * Add-to-cart is OPTIONAL: pass `onAdd` to render the corner add button
 * (CompleteTheLook + the modal). Link-only rails (YouMayLove, RecentlyViewed)
 * omit it and the tile is a pure <Link>.
 *
 * @param {object}   item        product ({ id, name, category, photo_urls|photo_url, selling_price, sale_price })
 * @param {string}   [testid]    data-testid for the card root (the <Link>)
 * @param {string}   [width]     Tailwind width/flex classes for the carousel slot
 * @param {function} [onAdd]     (e, item) => void — when present, renders the corner add button
 * @param {boolean}  [added]     true → show the green tick confirmation state
 * @param {string}   [addTestid] data-testid for the add button
 */
export default function RecoCard({
    item,
    testid,
    width = "w-[60%] sm:w-[42%] md:w-[31%] lg:w-[23%]",
    onAdd,
    added = false,
    addTestid,
}) {
    if (!item) return null;

    const img = imgSrc((item.photo_urls && item.photo_urls[0]) || item.photo_url);
    const onSale = isOnSale(item);
    const price = effectivePrice(item);
    const saveAmt = onSale ? Number(item.selling_price) - price : 0;

    return (
        <Link
            to={productPath(item)}
            data-testid={testid}
            className={`group flex flex-col flex-shrink-0 snap-start ${width}
                        bg-white rounded-2xl overflow-hidden border border-rosegold/25
                        shadow-[0_12px_34px_-20px_rgba(107,44,57,0.32)]
                        hover:shadow-[0_22px_50px_-22px_rgba(107,44,57,0.5)]
                        hover:border-rosegold/50 hover:-translate-y-0.5
                        transition-all duration-300`}
        >
            {/* Image sits AT THE TOP of a self-contained white card (the card,
                not the image, carries the border + shadow) so the tile reads as
                a distinct product card and never blends into the ivory page. */}
            <div className="relative aspect-[4/5] overflow-hidden bg-ivory-soft">
                <img
                    src={img}
                    alt={item.name}
                    loading="lazy"
                    className="w-full h-full object-cover object-top transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.06]"
                    onError={(e) => { e.target.src = "/default-item.svg"; }}
                />

                {onAdd && (
                    <>
                        {/* bottom scrim so the add button reads on any image */}
                        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-ink/35 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        <button
                            type="button"
                            onClick={(e) => onAdd(e, item)}
                            data-testid={addTestid}
                            aria-label={`Add ${item.name} to cart`}
                            className={`absolute bottom-2.5 right-2.5 h-11 rounded-full text-ivory flex items-center justify-center shadow-lg backdrop-blur-sm transition-all duration-300 active:scale-95 overflow-hidden
                                        ${added ? "w-11 bg-emerald-600" : "w-11 group-hover:w-[122px] bg-ink/90 hover:bg-rosegold"}`}
                        >
                            {added ? (
                                <Check size={18} strokeWidth={2.5} />
                            ) : (
                                <span className="inline-flex items-center gap-1.5 px-3 whitespace-nowrap">
                                    <Plus size={18} strokeWidth={2} className="flex-shrink-0" />
                                    <span className="text-[11px] font-bold uppercase tracking-wider max-w-0 group-hover:max-w-[80px] opacity-0 group-hover:opacity-100 transition-all duration-300">Add to bag</span>
                                </span>
                            )}
                        </button>
                    </>
                )}
            </div>

            <div className="p-3 sm:p-3.5 flex-1 flex flex-col">
                {item.category && (
                    <p className="text-[10.5px] uppercase tracking-[0.18em] text-rosegold font-bold mb-1 truncate">
                        {item.category}
                    </p>
                )}
                <h3 className="text-[13.5px] font-medium text-ink leading-snug line-clamp-2 min-h-[2.4rem]">{item.name}</h3>
                <div className="mt-1.5 flex items-baseline gap-2 flex-wrap min-w-0">
                    <span className="font-num text-[15px] font-bold text-ink leading-none tabular-nums tracking-tight">
                        {fmtINR(price)}
                    </span>
                    {onSale && (
                        <>
                            <span className="font-num text-[12px] text-ink-soft/60 line-through font-semibold tabular-nums">
                                {fmtINR(item.selling_price)}
                            </span>
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-wine/10 text-wine text-[10px] font-bold tracking-wide leading-none">
                                Save {fmtINR(saveAmt)}
                            </span>
                        </>
                    )}
                </div>
            </div>
        </Link>
    );
}
